// THE ACCOUNT CORNER: who you are, and the two things you do about it.
//
// # What was lost, and how
//
// Narrowing the rail to icons moved the tenant and the search to the topbar and DROPPED
// the sign-out entirely. It had lived at the bottom of the wide rail, correctly — beside
// the person it signs out — and the icon rail has no room for a labelled button, so it
// simply went. Nobody could leave the product.
//
// That is the ordinary shape of this mistake: a refactor moves three things out of a
// container, and the fourth is the one nobody listed.
//
// # An AVATAR is the affordance, and it is the person's picture WHERE THERE IS ONE
//
// This said "riksu holds no photograph — the contract has no field for one and the object
// store never receives one", and both halves of that are now out of date:
// `GET /v1/people/{person_id}/photograph` exists, and the object store grew the read half
// of its seam. So the trigger draws a photograph when riksu could hand one over.
//
// What has NOT changed is what happens when it could not, and that is still the design
// rather than a gap: two letters on a neutral fill. No silhouette and no generated
// cartoon — decoration standing in for a fact. The shell asks once, for the one person
// signed in; nothing here fetches per render and no list anywhere asks at all.
//
// # THE MENU CARRIES THE PERSON, not just their name
//
// Arda: *"Session olarak açtığın yer kişinin bilgilerini görselini falan barındırmalı"* —
// the place you open as your session should hold the person's details and their picture.
// So the header of this menu is an identity block: the picture at card size, the name,
// the company, and the way to the record that holds the rest. It was a name and a tenant
// string at caption size, which is a label rather than a person.
//
// # The tenant is IN here, not beside it
//
// It was a bare `acc_01jqz…` string in the bar: an identifier nobody reads, occupying the
// most valuable strip on the screen. A person needs to know which company they are in —
// riksu is multi-tenant and every figure belongs to one — but they need it when they
// think to ask, not continuously. So it is the first line of this menu, said in full.

import { IdCardIcon, LayoutDashboardIcon, LogOutIcon, SettingsIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { recordAt } from "../app/records";
import { clearWorkingDrafts } from "../lib/working-draft";
import { useT } from "../shell/i18n";
import { PersonAvatar } from "./avatar";
import { ON_SHELL_CONTROL } from "./button";
import { LanguageChoice } from "./language";
import { RecordLink } from "./record-link";

export interface AccountProps {
  /** Who is signed in. The principal, when riksu knows no person behind it. */
  readonly who: string;
  /** Which company. Riksu is multi-tenant and every figure on every screen is one's. */
  readonly tenant: string;
  /**
   * The employee this login IS, when riksu resolved one.
   *
   * **Absent is an ordinary answer**, not a gap: a person is who the employment
   * relationship is about and a principal is a login, and a support engineer, a partner
   * administrator and an integration principal all legitimately have the second and not
   * the first. So the record link is offered only where there is a record — the same
   * rule the org chart's `onAssign` follows, where a control that would be refused is
   * not drawn at all.
   *
   * The ID rather than a whole `PersonRef`, because the NAME is already `who`. Passing
   * both halves of one object would let a caller supply a name from one person and an
   * identifier from another, which is a defect the type would not catch.
   */
  readonly personId?: string;
  /**
   * A time-limited URL for their photograph, when riksu handed one over.
   *
   * Resolved by the SHELL and passed in, rather than fetched here, for the reason every
   * other read in this product is lifted out of the thing that draws it: this component
   * is mounted for the whole session and re-renders on every navigation, and a fetch
   * inside it would be a request per render of the topbar. The shell asks once, for the
   * one person signed in.
   *
   * Absent is ordinary and is not a loading state — see `ui/avatar.tsx`. Initials are the
   * answer, permanently, for a principal riksu resolves to no person and for a person
   * nobody has filed a photograph for.
   */
  readonly photograph?: string | null;
  /** Opens the one settings pane shared with the rail control. */
  readonly onSettings: () => void;
  /** Enters contextual editing mode for the page under the account menu. */
  readonly onPersonalize: () => void;
}

export function Account({
  who,
  tenant,
  personId,
  photograph,
  onSettings,
  onPersonalize,
}: AccountProps) {
  const t = useT();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            variant="ghost"
            size="icon"
            // A SQUARE TARGET holding a round avatar. The ghost fill is the only thing
            // that appears on hover; the avatar itself does not change, because a
            // photograph that moves is a photograph somebody is looking at rather than
            // a control they are aiming for.
            //
            // 32px, like every other control in the bar. It was 36 — a third size in a
            // strip that already held two — which is the drift a stated control height
            // exists to stop, and it was visible: the avatar sat a pixel lower than the
            // search icon beside it.
            //
            // The EXPANDED state gets its own fill. Without one the trigger stops
            // looking pressed the moment the pointer leaves it, while its menu is still
            // open underneath.
            // `ON_SHELL_CONTROL` AND NOT A COPY OF IT. This carried
            // `hover:bg-surface-shell-hover aria-expanded:bg-surface-shell-selected` —
            // the two background halves of that constant and none of the three colour
            // halves — because the constant lived in `app/app.tsx` and `ui/` cannot
            // import from `app/`, so retyping it was the only way to dress this control.
            // Nothing rendered wrongly: the only child is a `PersonAvatar` that paints its
            // own `bg-surface-panel text-foreground` and inherits none of it. What was
            // wrong is that the guarantee had already failed, and the next control would
            // have been copied from this one.
            className={`shrink-0 rounded-full p-0 ${ON_SHELL_CONTROL}`}
            aria-label={t("account.open")}
          >
            {/* `PersonAvatar` AND NOT `components/ui/avatar.tsx`, which this was.
                shadcn's is a generated component and the size had to be put on a `<span>`
                INSIDE `AvatarFallback` to survive `cn()`: `tailwind-merge` cannot know
                that `--text-shell-caption` is a font size, so it files every `text-*` name
                riksu owns under COLOUR and keeps only the last one — the initials rendered
                at the generated `text-sm`, fourteen points inside a twenty-eight-pixel
                circle. Riksu's own component names one class per concern and the merge
                never arises, which is a better fix than remembering where to put the span.
                The size stays 28px, like every other control in the bar. */}
            <PersonAvatar
              name={who}
              photograph={photograph}
              size="md"
              className="bg-surface-panel text-foreground"
            />
          </Button>
        }
      />
      <DropdownMenuContent align="end" className="w-64">
        {/* WHO, THEN WHICH COMPANY. The second was a bare identifier in the bar, which
            is not a company name and was occupying the most valuable strip on screen.
            Here it is out of the way and still one press from anybody who wonders. */}
        {/* A HEADER, AND DELIBERATELY NOT A `DropdownMenuLabel`.

            It was one, and it THREW: that component is Base UI's `Menu.GroupLabel`,
            which reads `MenuGroupContext` and raises when there is none. So pressing the
            avatar was an uncaught error that took the application to the router's error
            screen — the menu had never opened successfully, and no test opened it.

            The fix is not to wrap it in a `DropdownMenuGroup`. A group label is the
            accessible NAME of a group of items, and this names none: it says who is
            signed in and which company they are in, and a separator follows it. A group
            containing a label and no items is a group of nothing, which is the shape
            that passes a lint and says something false to a screen reader.

            So it is a plain element carrying the same measurements the generated label
            uses, and the menu's own name comes from the trigger. `language.tsx` had the
            same throw with the opposite fix, because its label DOES name the radio items
            under it — the two are worth reading together. */}
        {/* THE PICTURE IS IN HERE TOO, at card size, and it is not a repetition of the
            trigger. The trigger is a 28px control somebody aims at; this is the one place
            in the shell that answers "who am I signed in as", and answering it with two
            letters at caption size is what made the product feel like it did not know.
            The name moves up a step to `shell-label` for the same reason: it is the
            subject of this menu, not a caption on it. */}
        <div className="flex items-center gap-3 px-1.5 py-1.5">
          <PersonAvatar name={who} photograph={photograph} size="lg" />
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="truncate text-shell-label font-medium text-foreground">{who}</span>
            <span className="truncate text-shell-caption text-muted-foreground">{tenant}</span>
          </div>
        </div>
        <DropdownMenuSeparator />
        {/* WHICH LANGUAGE YOU READ IN is a fact about you, like which company you are in
            and how you leave — so it belongs in this menu and not in the bar. The same
            preference is available from Settings; both write through one locale owner. */}
        <LanguageChoice />
        <DropdownMenuSeparator />
        {/* YOUR OWN RECORD, and only where riksu resolved one.

            The name above says who riksu thinks you are; this is the way to what it
            HOLDS about you, which is the question the name prompts. It is the same
            `/people/{id}` a table cell opens, through the same `RecordLink`, so the page
            underneath survives — pressing it from /leave leaves you on /leave with the
            record over it.

            Not rendered at all when `personId` is absent. A support engineer HAS no
            employment record, so a greyed control would promise them a surface that will
            never exist for them. Absent means not yours. */}
        {personId ? (
          <DropdownMenuItem render={<RecordLink to={recordAt("person", personId)} />} closeOnClick>
            <IdCardIcon className="size-4" aria-hidden="true" />
            {t("account.myRecord")}
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuItem onClick={onPersonalize} closeOnClick>
          <LayoutDashboardIcon className="size-4" aria-hidden="true" />
          {t("personalization.start")}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onSettings} closeOnClick>
          <SettingsIcon className="size-4" aria-hidden="true" />
          {t("account.settings")}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {/* A REAL FORM POST, not a link. Signing out is a write — it destroys a session
            — and a GET that changes state is one a prefetcher can fire by itself. */}
        <form method="post" action="/auth/logout" onSubmit={clearWorkingDrafts}>
          <button
            type="submit"
            className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-shell-body outline-none hover:bg-surface-hover focus-visible:bg-surface-hover"
          >
            <LogOutIcon className="size-4" aria-hidden="true" />
            {t("app.signOut")}
          </button>
        </form>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
